// Mock data for the prototype. All people and organisations are fictional.

export type PersonId = string;

export interface Person {
  id: PersonId;
  name: string;
  title?: string;
  /** Organisation name for people outside the user's organisation; "Guest" for no organisation. */
  external?: string;
  avatar: AvatarHue;
  online?: boolean;
  lastSeen?: string;
  /** Has a profile photo (the prototype draws a placeholder portrait). */
  photo?: boolean;
}

export type AvatarHue = 'orange' | 'green' | 'blue' | 'coral' | 'teal' | 'purple';

export const ME = 'me';

export const people: Record<PersonId, Person> = {
  me: { id: 'me', name: 'Anna Berg', title: 'Head of Dispatch', avatar: 'orange' },
  jonas: { id: 'jonas', name: 'Jonas Weber', title: 'Fleet Manager', avatar: 'blue', online: true },
  lea: { id: 'lea', name: 'Lea Hoffmann', title: 'Dispatcher', avatar: 'teal', online: true },
  mehmet: {
    id: 'mehmet',
    name: 'Mehmet Yılmaz',
    title: 'Warehouse Lead',
    avatar: 'green',
    lastSeen: 'today at 08:12',
  },
  sophie: { id: 'sophie', name: 'Sophie Krüger', title: 'Customer Service', avatar: 'purple' },
  tobias: { id: 'tobias', name: 'Tobias Brandt', title: 'CEO', avatar: 'blue' },
  clara: { id: 'clara', name: 'Clara Neumann', title: 'HR', avatar: 'coral' },
  felix: { id: 'felix', name: 'Felix Lange', title: 'Driver', avatar: 'orange' },
  jana: { id: 'jana', name: 'Jana Schulz', title: 'Dispatcher', avatar: 'green' },
  lukas: {
    id: 'lukas',
    name: 'Lukas Meyer',
    title: 'Terminal Operations',
    external: 'HafenTerminal AG',
    avatar: 'teal',
  },
  miriam: {
    id: 'miriam',
    name: 'Dr. Miriam Falk',
    title: 'Occupational physician',
    external: 'Praxis am Park',
    avatar: 'purple',
  },
  paul: { id: 'paul', name: 'Paul Schreiber', external: 'Guest', avatar: 'coral' },
  nina: { id: 'nina', name: 'Nina Albers', external: 'Guest', avatar: 'green' },
  martin: { id: 'martin', name: 'Martin Graf', external: 'Spedition Graf', avatar: 'blue' },
};

export type Illustration = 'harbour' | 'trucks' | 'trucks2' | 'pallets' | 'team' | 'chart';

export interface Reaction {
  emoji: string;
  by: PersonId[];
}

export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'read';

export interface Message {
  id: string;
  from: PersonId;
  time: string;
  day?: string;
  kind: 'text' | 'image' | 'document' | 'voice' | 'system' | 'call';
  text?: string;
  replyTo?: string;
  reactions?: Reaction[];
  status?: MessageStatus;
  edited?: boolean;
  image?: Illustration;
  doc?: { name: string; size: string; type: 'pdf' | 'xlsx' | 'docx' };
  voice?: { seconds: number; waveform: number[] };
}

export interface Chat {
  id: string;
  kind: 'direct' | 'group';
  name?: string;
  members: PersonId[];
  avatar?: AvatarHue;
  description?: string;
  pinned?: boolean;
  muted?: boolean;
  unread?: number;
  typing?: PersonId;
  liveCall?: { participants: PersonId[]; startedAt: string };
  messages: Message[];
}

function wave(seed: number, n = 40): number[] {
  const out: number[] = [];
  let s = seed;
  for (let i = 0; i < n; i++) {
    s = (s * 9301 + 49297) % 233280;
    const r = s / 233280;
    const envelope = Math.sin((i / n) * Math.PI) * 0.6 + 0.4;
    out.push(Math.max(0.12, Math.min(1, r * envelope + 0.1)));
  }
  return out;
}
export { wave };

export const initialChats: Chat[] = [
  {
    id: 'dispatch',
    kind: 'group',
    name: 'Dispatch Team',
    avatar: 'orange',
    description: 'Daily coordination for the Hamburg depot',
    members: ['me', 'jonas', 'lea', 'mehmet', 'felix', 'jana'],
    pinned: true,
    unread: 3,
    liveCall: { participants: ['jonas', 'lea', 'felix'], startedAt: '10:24' },
    messages: [
      {
        id: 'd1',
        from: 'jonas',
        day: 'Yesterday',
        time: '17:40',
        kind: 'text',
        text: 'Tomorrow’s route plan is in the shared folder. Please check the A7 detours.',
      },
      {
        id: 'd2',
        from: 'me',
        time: '17:52',
        kind: 'text',
        text: 'Thanks, will look first thing.',
        status: 'read',
      },
      {
        id: 'd3',
        from: 'mehmet',
        day: 'Today',
        time: '08:05',
        kind: 'image',
        image: 'pallets',
        text: 'Gate 4 is clear again, all pallets for Bremen are staged.',
        reactions: [
          { emoji: '👍', by: ['jonas', 'lea', 'me'] },
          { emoji: '🎉', by: ['jana'] },
        ],
      },
      {
        id: 'd4',
        from: 'lea',
        time: '08:11',
        kind: 'text',
        text: 'Perfect. Felix, can you take the 10:30 slot?',
        replyTo: 'd3',
      },
      {
        id: 'd5',
        from: 'felix',
        time: '08:14',
        kind: 'voice',
        voice: { seconds: 23, waveform: wave(3) },
      },
      {
        id: 'd6',
        from: 'jana',
        time: '09:58',
        kind: 'text',
        text: 'Customer in Lübeck moved their window to 14:00–16:00.',
      },
      { id: 'd7', from: 'jonas', time: '10:24', kind: 'call', text: 'Jonas started a group call' },
      {
        id: 'd8',
        from: 'lea',
        time: '10:26',
        kind: 'text',
        text: 'Joining in a sec — quick sync on Lübeck?',
      },
    ],
  },
  {
    id: 'jonas',
    kind: 'direct',
    members: ['me', 'jonas'],
    pinned: true,
    messages: [
      {
        id: 'j1',
        from: 'jonas',
        day: 'Today',
        time: '09:02',
        kind: 'text',
        text: 'Morning! Did you see the fuel report for October?',
      },
      {
        id: 'j2',
        from: 'me',
        time: '09:05',
        kind: 'text',
        text: 'Not yet. Can you send it over?',
        status: 'read',
      },
      {
        id: 'j3',
        from: 'jonas',
        time: '09:06',
        kind: 'document',
        doc: { name: 'Fuel report October.pdf', size: '1.2 MB', type: 'pdf' },
      },
      {
        id: 'j4',
        from: 'jonas',
        time: '09:06',
        kind: 'text',
        text: 'Consumption is down 6% since the new tyres 🙌',
        reactions: [{ emoji: '🔥', by: ['me'] }],
      },
      {
        id: 'j5',
        from: 'me',
        time: '09:20',
        kind: 'voice',
        voice: { seconds: 41, waveform: wave(11) },
        status: 'read',
      },
      {
        id: 'j6',
        from: 'jonas',
        time: '09:24',
        kind: 'text',
        text: 'Agreed. Let’s show it in Thursday’s meeting.',
        replyTo: 'j5',
      },
      {
        id: 'j7',
        from: 'me',
        time: '09:31',
        kind: 'text',
        text: 'Great, I’ll add it to the agenda.',
        status: 'delivered',
      },
    ],
  },
  {
    id: 'elbe',
    kind: 'group',
    name: 'Project Elbe',
    avatar: 'teal',
    description: 'Container handover with HafenTerminal AG',
    members: ['me', 'jonas', 'lukas', 'sophie'],
    unread: 1,
    messages: [
      {
        id: 'e1',
        from: 'me',
        day: 'Monday',
        time: '14:10',
        kind: 'system',
        text: 'Anna added Lukas Meyer (HafenTerminal AG)',
      },
      {
        id: 'e2',
        from: 'lukas',
        time: '14:22',
        kind: 'text',
        text: 'Hello everyone, thanks for adding me. The new slot booking goes live on 1 December.',
      },
      {
        id: 'e3',
        from: 'sophie',
        time: '14:30',
        kind: 'text',
        text: 'Great news. Do you have the API documentation for the booking system?',
      },
      {
        id: 'e4',
        from: 'lukas',
        day: 'Today',
        time: '10:02',
        kind: 'document',
        doc: { name: 'Slot booking v2.xlsx', size: '86 KB', type: 'xlsx' },
        text: 'Here’s the slot schedule for December.',
      },
    ],
  },
  {
    id: 'lea',
    kind: 'direct',
    members: ['me', 'lea'],
    typing: 'lea',
    unread: 2,
    messages: [
      {
        id: 'l1',
        from: 'lea',
        day: 'Today',
        time: '10:12',
        kind: 'text',
        text: 'Do you have five minutes after lunch?',
      },
      { id: 'l2', from: 'lea', time: '10:13', kind: 'text', text: 'It’s about the holiday rota.' },
    ],
  },
  {
    id: 'miriam',
    kind: 'direct',
    members: ['me', 'miriam'],
    messages: [
      {
        id: 'm1',
        from: 'miriam',
        day: 'Yesterday',
        time: '11:30',
        kind: 'text',
        text: 'The eye tests for the drivers can take place on 18 November. I’ve reserved the morning.',
      },
      {
        id: 'm2',
        from: 'me',
        time: '11:42',
        kind: 'text',
        text: 'Thank you, I’ll send you the list of names.',
        status: 'read',
      },
    ],
  },
  {
    id: 'sophie',
    kind: 'direct',
    members: ['me', 'sophie'],
    muted: true,
    messages: [
      {
        id: 's1',
        from: 'sophie',
        day: 'Yesterday',
        time: '16:03',
        kind: 'image',
        image: 'trucks',
        text: 'The new vans look great!',
      },
    ],
  },
  {
    id: 'social',
    kind: 'group',
    name: 'Kantine & Social',
    avatar: 'green',
    members: ['me', 'clara', 'mehmet', 'sophie', 'jana', 'felix'],
    muted: true,
    unread: 12,
    messages: [
      {
        id: 'k1',
        from: 'clara',
        day: 'Today',
        time: '09:40',
        kind: 'text',
        text: 'Soup of the day: pumpkin 🎃',
      },
    ],
  },
  {
    id: 'paul',
    kind: 'direct',
    members: ['me', 'paul'],
    messages: [
      {
        id: 'p1',
        from: 'me',
        day: 'Friday',
        time: '15:00',
        kind: 'text',
        text: 'Hi Paul, here’s the invitation for the driver interview on Monday.',
        status: 'read',
      },
      { id: 'p2', from: 'paul', time: '15:20', kind: 'text', text: 'Thank you! See you then.' },
    ],
  },
];

export interface ChatRequest {
  id: string;
  from: PersonId;
  time: string;
  preview: string;
  via?: string;
}

export const initialRequests: ChatRequest[] = [
  {
    id: 'r1',
    from: 'martin',
    time: '09:12',
    preview: 'Hello Ms Berg, we’d like to talk about subcontracting the Bremen route.',
    via: 'Found you by email address',
  },
  {
    id: 'r2',
    from: 'nina',
    time: 'Yesterday',
    preview: 'Hi Anna, we met at the logistics fair in Munich. Is the dispatcher job still open?',
    via: 'Found you by email address',
  },
];

// ---------------------------------------------------------------------------------------------
// Feed

export interface Channel {
  id: string;
  name: string;
  official: boolean;
  avatar: AvatarHue;
  recommended?: boolean;
}

export const channels: Record<string, Channel> = {
  ceo: { id: 'ceo', name: 'CEO Office', official: true, avatar: 'blue', recommended: true },
  hr: { id: 'hr', name: 'HR News', official: true, avatar: 'coral', recommended: true },
  it: { id: 'it', name: 'IT Service', official: true, avatar: 'purple' },
};

export interface Poll {
  options: { id: string; label: string; votes: number }[];
  endsIn: string;
  anonymous: boolean;
  voted?: string;
}

export interface Post {
  id: string;
  author: PersonId;
  channel?: string;
  time: string;
  text: string;
  images?: Illustration[];
  link?: { title: string; domain: string; image?: Illustration };
  poll?: Poll;
  pinned?: boolean;
  expires?: string;
  likes: number;
  liked?: boolean;
  replies: number;
  reposts: number;
  reposted?: boolean;
  bookmarked?: boolean;
  views: number;
  following: boolean;
}

export const initialPosts: Post[] = [
  {
    id: 'f1',
    author: 'tobias',
    channel: 'ceo',
    time: '2h',
    pinned: true,
    text: 'A record October: 18,400 deliveries, 97.8% on time. Thank you all for an outstanding month. The Elbe terminal partnership starts in December — more at Thursday’s all-hands. #nordwind #thankyou',
    images: ['harbour'],
    likes: 214,
    replies: 31,
    reposts: 12,
    views: 1840,
    following: true,
  },
  {
    id: 'f2',
    author: 'clara',
    channel: 'hr',
    time: '4h',
    text: 'When should we hold this year’s Christmas party?',
    poll: {
      options: [
        { id: 'a', label: 'Friday, 11 December', votes: 96 },
        { id: 'b', label: 'Saturday, 12 December', votes: 41 },
        { id: 'c', label: 'Friday, 18 December', votes: 63 },
      ],
      endsIn: '2 days left',
      anonymous: true,
    },
    likes: 58,
    replies: 14,
    reposts: 2,
    views: 1201,
    following: true,
  },
  {
    id: 'f3',
    author: 'lea',
    time: '5h',
    text: 'Our first two electric trucks arrived at the Hamburg depot this morning ⚡ Charging points at gates 2 and 3 are live.',
    images: ['trucks', 'trucks2'],
    likes: 132,
    liked: true,
    replies: 18,
    reposts: 9,
    views: 980,
    following: true,
  },
  {
    id: 'f4',
    author: 'mehmet',
    time: '6h',
    expires: 'Expires in 42 h',
    text: 'Reminder: forklift safety refresher tomorrow 07:30 in hall B. Please bring your licence. #safetyfirst',
    likes: 27,
    replies: 3,
    reposts: 4,
    views: 412,
    following: false,
  },
  {
    id: 'f5',
    author: 'sophie',
    channel: 'it',
    time: '1d',
    text: 'Phishing mails are going around that pretend to come from our fuel card provider. Never enter your PIN via a link. The 10-minute training is here:',
    link: {
      title: 'Spot a phishing mail in 10 minutes',
      domain: 'learning.nordwind-logistik.de',
      image: 'chart',
    },
    likes: 45,
    replies: 6,
    reposts: 21,
    views: 1530,
    following: false,
  },
];

export interface FeedReply {
  id: string;
  postId: string;
  author: PersonId;
  time: string;
  text: string;
  likes: number;
  replies?: FeedReply[];
}

export const feedReplies: FeedReply[] = [
  {
    id: 'fr1',
    postId: 'f1',
    author: 'jonas',
    time: '1h',
    text: 'Proud of this team. The new route planning made a real difference.',
    likes: 24,
    replies: [
      {
        id: 'fr1a',
        postId: 'f1',
        author: 'lea',
        time: '48m',
        text: 'And the drivers who handled the A7 closures 👏',
        likes: 11,
      },
    ],
  },
  {
    id: 'fr2',
    postId: 'f1',
    author: 'mehmet',
    time: '1h',
    text: 'Warehouse crew says thanks for the pizza on Friday 🍕',
    likes: 19,
  },
  {
    id: 'fr3',
    postId: 'f1',
    author: 'jana',
    time: '32m',
    text: 'Will the all-hands be streamed for the night shift?',
    likes: 7,
  },
];

// ---------------------------------------------------------------------------------------------
// Calls

export interface CallLog {
  id: string;
  with: PersonId[];
  groupName?: string;
  direction: 'incoming' | 'outgoing' | 'missed';
  video: boolean;
  time: string;
  duration?: string;
}

export const callLog: CallLog[] = [
  {
    id: 'cl1',
    with: ['jonas', 'lea', 'felix'],
    groupName: 'Dispatch Team',
    direction: 'incoming',
    video: true,
    time: 'Today, 10:24',
    duration: 'Live now',
  },
  { id: 'cl2', with: ['lea'], direction: 'missed', video: false, time: 'Today, 09:47' },
  {
    id: 'cl3',
    with: ['miriam'],
    direction: 'outgoing',
    video: false,
    time: 'Yesterday, 11:28',
    duration: '4:12',
  },
  {
    id: 'cl4',
    with: ['jonas'],
    direction: 'incoming',
    video: true,
    time: 'Yesterday, 08:15',
    duration: '12:40',
  },
  {
    id: 'cl5',
    with: ['lukas'],
    direction: 'outgoing',
    video: false,
    time: 'Monday, 14:05',
    duration: '7:02',
  },
  { id: 'cl6', with: ['tobias'], direction: 'missed', video: true, time: 'Friday, 17:30' },
];

/** People in the company directory, for starting a new chat. */
export const directory: PersonId[] = [
  'jonas',
  'lea',
  'mehmet',
  'sophie',
  'tobias',
  'clara',
  'felix',
  'jana',
];

/** What a user without an organisation sees: only chats they were invited to. */
export const guestChats: Chat[] = [
  {
    id: 'g-anna',
    kind: 'direct',
    members: ['me', 'anna-ext'],
    messages: [
      {
        id: 'ga1',
        from: 'anna-ext',
        day: 'Friday',
        time: '15:00',
        kind: 'text',
        text: 'Hi Paul, here’s the invitation for the driver interview on Monday.',
      },
      {
        id: 'ga2',
        from: 'me',
        time: '15:20',
        kind: 'text',
        text: 'Thank you! See you then.',
        status: 'read',
      },
    ],
  },
];

export const guestPeople: Record<PersonId, Person> = {
  'anna-ext': {
    id: 'anna-ext',
    name: 'Anna Berg',
    title: 'Head of Dispatch',
    external: 'Nordwind Logistik',
    avatar: 'orange',
  },
};

export const RECOVERY_KEY = 'EsTc 9Kq2 HfVx 3mPa Lw7R Zt4N bYd8 Qe6U Jg5C 2nXs Fh9W';

/**
 * The organisation policy "Require app lock" (set in the admin portal, on by default). Praxis am
 * Park has switched it off to show the optional variant; people without an organisation choose.
 */
export const appLockRequired: Record<string, boolean> = {
  nordwind: true,
  praxis: false,
  custom: true,
};

export function requiresAppLock(orgId: string | null): boolean {
  return orgId === null ? false : (appLockRequired[orgId] ?? true);
}
